import { useState } from "react";
/**
 * @fileoverview User Management page component.
 *
 * Displays user management tables for Sector Admins and Plant Users based on
 * current user permissions. Includes sorting and pagination functionality.
 */

import {
	Alert,
	Box,
	Paper,
	Stack,
	TablePagination,
	Typography,
} from "@mui/material";
import { UseHasPermission } from "lib/hooks/useHasPermission";
import { UseSort } from "lib/hooks/useSort";
import { MuiTablePagination } from "lib/styles/pagination";
import { PlantUsersTable } from "./Components/Tables/PlantUsersTable";
import { SectorAdminsTable } from "./Components/Tables/SectorAdminsTable";
import { UseBusinessObjects } from "./hooks/useBusinessObjects";
import { UsePagination } from "./hooks/usePagination";
import { UseUserManagement } from "./hooks/useUserManagement";
import { AddPlantUserDialog } from "./Components/Dialogs/AddPlantUserDialog";
import { EditPlantUserDialog } from "./Components/Dialogs/EditPlantUserDialog";
import { UseCurrentUserPermissions } from "lib/hooks";
import { AddSectorAdminDialog } from "./Components/Dialogs/AddSectorAdminDialog";

export function UserManagement() {
	const permissions = UseHasPermission();
	// Trigger fetch of all business objects so dialogs and SectorTypePlantPermissionTable can use the list
	UseBusinessObjects();
	const { sectorAdmins, plantUsers, loading, error, refetch } =
		UseUserManagement();
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [addSectorAdminDialogOpen, setAddSectorAdminDialogOpen] =
		useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editingGmin, setEditingGmin] = useState<string | null>(null);

	const {
		sorted: sortedSectorAdmins,
		requestSort: sectorAdminRequestSort,
		sortEnum: sectorAdminSortEnum,
	} = UseSort(sectorAdmins, "name");

	const {
		sorted: sortedPlantUsers,
		requestSort: plantUserRequestSort,
		sortEnum: plantUserSortEnum,
	} = UseSort(plantUsers, "name");

	const sectorAdminPagination = UsePagination(sortedSectorAdmins, 10);
	const plantUserPagination = UsePagination(sortedPlantUsers, 10);

	const handleOpenAddDialog = () => {
		setAddDialogOpen(true);
	};

	const handleCloseAddDialog = () => {
		setAddDialogOpen(false);
	};

	const handleOpenEditDialog = (gmin: string) => {
		setEditingGmin(gmin);
		setEditDialogOpen(true);
	};

	const handleCloseEditDialog = () => {
		setEditDialogOpen(false);
		setEditingGmin(null);
	};

	const handleDialogSuccess = () => {
		refetch();
	};
	if (!permissions.canViewSectorAdmins && !permissions.canViewPlantUsers) {
		return (
			<Box sx={{ px: 3, pt: 4, pb: 2, textAlign: "center" }}>
				<Typography					
					component="p"
					variant="h5"
					sx={{
						color: "error.main",
						fontWeight: "bold",
					}}
				>
					You currently do not have permission to access User Management.  Please contact one of the sector admins or plant admins for access.
				</Typography>
			</Box>			
		);
	}

	if (error) {
		return (
			<Box p={3}>
				<Alert severity="error">{error}</Alert>
			</Box>
		);
	}

	const handleOpenAddSectorAdminDialog = () => {
		setAddSectorAdminDialogOpen(true);
	};

	const handleCloseAddSectorAdminDialog = () => {
		setAddSectorAdminDialogOpen(false);
	};

	return (
		<Box p={3}>
			<Stack spacing={4}>
				<Typography variant="h1">User Management</Typography>

				{permissions.canViewSectorAdmins && (
					<Paper>
						<SectorAdminsTable
							rows={sectorAdminPagination.pagedItems}
							order={sectorAdminSortEnum}
							onRequestSort={sectorAdminRequestSort}
							loading={loading}
							page={0}
							rowsPerPage={sectorAdminPagination.rowsPerPage}
							title="Sector Admins"
							onAddClick={handleOpenAddSectorAdminDialog}
							onDeleteSuccess={handleDialogSuccess}
						/>
						{sectorAdminPagination.totalCount > 0 && (
							<TablePagination
								rowsPerPageOptions={[5, 10, 25]}
								component="div"
								count={sectorAdminPagination.totalCount}
								rowsPerPage={sectorAdminPagination.rowsPerPage}
								page={sectorAdminPagination.page}
								onPageChange={
									sectorAdminPagination.handleChangePage
								}
								onRowsPerPageChange={
									sectorAdminPagination.handleChangeRowsPerPage
								}
								sx={{
									...MuiTablePagination(),
								}}
							/>
						)}
					</Paper>
				)}

				{permissions.canViewPlantUsers && (
					<Paper>
						<PlantUsersTable
							rows={plantUserPagination.pagedItems}
							order={plantUserSortEnum}
							onRequestSort={plantUserRequestSort}
							loading={loading}
							page={0}
							rowsPerPage={plantUserPagination.rowsPerPage}
							title="Plant Users"
							onAddClick={(permissions.isSectorAdmin || permissions.isPlantUsersAccessManager) ? handleOpenAddDialog : undefined}
							onEditClick={(permissions.isSectorAdmin || permissions.isPlantUsersAccessManager) ? handleOpenEditDialog : undefined}
							onDeleteSuccess={refetch}
						/>
						{plantUserPagination.totalCount > 0 && (
							<TablePagination
								rowsPerPageOptions={[5, 10, 25]}
								component="div"
								count={plantUserPagination.totalCount}
								rowsPerPage={plantUserPagination.rowsPerPage}
								page={plantUserPagination.page}
								onPageChange={
									plantUserPagination.handleChangePage
								}
								onRowsPerPageChange={
									plantUserPagination.handleChangeRowsPerPage
								}
								sx={{
									...MuiTablePagination(),
								}}
							/>
						)}
					</Paper>
				)}
			</Stack>

			{/* Add Plant User Dialog */}
			<AddPlantUserDialog
				open={addDialogOpen}
				onClose={handleCloseAddDialog}
				onSuccess={handleDialogSuccess}
				existingSectorAdmins={sectorAdmins}
				existingPlantUsers={plantUsers}
			/>

			{/* Edit Plant User Dialog */}
			<EditPlantUserDialog
				open={editDialogOpen}
				gmin={editingGmin}
				onClose={handleCloseEditDialog}
				onSuccess={handleDialogSuccess}
				sectorIdsUserIsSectorAdminFor={
					editingGmin
						? (sectorAdmins.find((r) => r.userId === editingGmin)?.sectorIds ?? [])
						: []
				}
			/>
			{/* Add Sector Admin Dialog */}
			<AddSectorAdminDialog
				open={addSectorAdminDialogOpen}
				onClose={handleCloseAddSectorAdminDialog}
				onSuccess={handleDialogSuccess}
				existingSectorAdminUserIds={sectorAdmins.map((r) => r.userId)}
			/>
		</Box>
	);
}
